// Curated fair-demo dataset generator. Self-contained (no imports from the app),
import { DEMO_USER } from './demo-mode'
// consumed by fixtures.js. Builds a coherent world:
//   2 properties x 2 nuclei x 2 barns (8 barns / 8 devices)
//   - Property A "Granja Sao Jose": SMAAI 5 (model smaai5, base null)
//       - Nucleo 1: running flocks (~age 34)
//       - Nucleo 2: CLEAR period (no running flock)
//   - Property B "Granja Boa Vista": InobramX family
//       - Nucleo Smart: SMAAI 5 SMART (smaai5smart) running flocks (~age 27)
//       - Nucleo Pro:   SMAAI 5 PRO   (smaai5pro)   running flocks (~age 41)
// Flocks carry weighing history + precomputed projection, mortality, silos and
// live telemetry so the dashboard, weighing/projection and KPIs all light up.

// ---------------------------------------------------------------------------
// Reference growth curves (age-indexed target body weight in grams, as-hatched)
// ---------------------------------------------------------------------------
const COBB500_WEIGHTS = [
  42, 56, 72, 89, 109, 131, 156, 184, 215, 249, 286, 326, 369, 415, 465, 518,
  574, 633, 695, 760, 828, 899, 972, 1048, 1126, 1207, 1290, 1375, 1462, 1551,
  1642, 1734, 1828, 1923, 2019, 2117, 2215, 2314, 2414, 2514, 2615, 2716, 2817,
  2918, 3019, 3119, 3219, 3318, 3416, 3513, 3609
]

const ROSS308_WEIGHTS = COBB500_WEIGHTS.map(function (w, age) {
  return Math.round(w * (age < 21 ? 1.01 : 1.02))
})

// Cumulative target FCR by age (interpolated from breeder tables).
function buildFcr () {
  const anchors = { 0: 0, 7: 0.86, 14: 1.08, 21: 1.28, 28: 1.46, 35: 1.62, 42: 1.78, 49: 1.94 }
  const keys = Object.keys(anchors).map(Number)
  const fcr = []
  for (let age = 0; age <= 50; age++) {
    let lo = keys[0]
    let hi = keys[keys.length - 1]
    for (let i = 0; i < keys.length - 1; i++) {
      if (age >= keys[i] && age <= keys[i + 1]) {
        lo = keys[i]
        hi = keys[i + 1]
        break
      }
    }
    const span = hi - lo || 1
    const t = (age - lo) / span
    fcr.push(Number((anchors[lo] + (anchors[hi] - anchors[lo]) * t).toFixed(2)))
  }
  return fcr
}

const FCR_CURVE = buildFcr()

export const breedCurves = [
  {
    id: 'curve-cobb500',
    strain: 'cobb500',
    sex: 'as_hatched',
    label: 'Cobb 500',
    source: 'Cobb-Vantress Broiler Performance & Nutrition Supplement',
    source_url: 'https://www.cobb-vantress.com',
    source_page: 3,
    max_age: 50,
    weights: COBB500_WEIGHTS,
    fcr: FCR_CURVE
  },
  {
    id: 'curve-ross308',
    strain: 'ross308',
    sex: 'as_hatched',
    label: 'Ross 308',
    source: 'Aviagen Ross 308 Broiler Performance Objectives',
    source_url: 'https://www.aviagen.com',
    source_page: 4,
    max_age: 50,
    weights: ROSS308_WEIGHTS,
    fcr: FCR_CURVE
  }
]

function curveWeights (strain) {
  return strain === 'ross308' ? ROSS308_WEIGHTS : COBB500_WEIGHTS
}

// Alarm code -> pt-BR description (GET translators/alarms), used to translate
// the InobramX active-alarm code array (a_a_c) during the alarm theater.
export const alarmTranslators = [
  { code: 1, description: 'Temperatura alta' },
  { code: 2, description: 'Temperatura baixa' },
  { code: 3, description: 'Umidade alta' },
  { code: 4, description: 'Umidade baixa' },
  { code: 5, description: 'CO2 alto' },
  { code: 8, description: 'Falta de energia' },
  { code: 138, description: 'Desarme de cortina por temperatura' },
  { code: 200, description: 'Nivel baixo de racao no silo' }
]

// ---------------------------------------------------------------------------
// Topology
// ---------------------------------------------------------------------------
const CITY_A = { id: 'city-cha', description: 'Chapeco' }
const CITY_B = { id: 'city-con', description: 'Concordia' }
const STATE_SC = { id: 'state-sc', description: 'Santa Catarina', initials: 'SC' }
const COUNTRY_BR = { id: 'country-br', description: 'Brasil' }

function nucleus (id, description, model, base, pretty, clear, strain, ages) {
  return {
    id: id,
    description: description,
    model: model,
    base: base,
    pretty: pretty,
    clear: clear,
    strain: strain,
    ages: ages
  }
}

const topology = [
  {
    id: 'prop-a',
    description: 'Granja Sao Jose',
    city: CITY_A,
    nuclei: [
      nucleus('nuc-a1', 'Nucleo 1', 'smaai5', null, 'SMAAI 5', false, 'cobb500', [34, 33]),
      nucleus('nuc-a2', 'Nucleo 2', 'smaai5', null, 'SMAAI 5', true, 'cobb500', [null, null])
    ]
  },
  {
    id: 'prop-b',
    description: 'Granja Boa Vista',
    city: CITY_B,
    nuclei: [
      nucleus('nuc-b1', 'Nucleo Smart', 'smaai5smart', 'inobramx', 'SMAAI 5 SMART', false, 'ross308', [27, 28]),
      nucleus('nuc-b2', 'Nucleo Pro', 'smaai5pro', 'inobramx', 'SMAAI 5 PRO', false, 'cobb500', [41, 40])
    ]
  }
]

function macFor (index) {
  const hex = (index + 1).toString(16).padStart(2, '0').toUpperCase()
  return 'A0:14:2D:6A:00:' + hex
}

// ---------------------------------------------------------------------------
// Build registries
// ---------------------------------------------------------------------------
export const properties = []
export const nucleiFlat = []
export const barnsFlat = []
export const devicesFlat = []
export const batchesByBarnId = {}
export const weightAnalysisByBatchId = {}
export const projectionByBatchId = {}
export const mortalitiesByBatchId = {}
export const silosByBarnId = {}
export const deviceFeatures = []
export const liveNodesByMac = {}
export const currentAgeByMac = {}
export const flockFactorByMac = {}
export const statisticsByMac = {}
export const inobramxMaxMinByMac = {}
export const inobramxExternalMaxMinByMac = {}
export const inobramxTempSetpointsByMac = {}
export const inobramxHumSetpointsByMac = {}
export const inobramxWaterByMac = {}
export const alarmHistoryAll = []

function isoDate (daysAgo) {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - daysAgo)
  return d.toISOString().slice(0, 10)
}

// Deterministic PRNG so the demo world is byte-identical on every rebuild while
// still looking organic. FNV-1a hash seeds a mulberry32 generator.
function hashString (str) {
  let h = 2166136261
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function seeded (seed) {
  let s = seed >>> 0
  return function () {
    s = (s + 0x6D2B79F5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function clamp (value, lo, hi) {
  return Math.min(hi, Math.max(lo, value))
}

// Curated, agronomically-coherent scenario per running flock, so the batch
// insights tell a real story that points the producer to a concrete action:
//   heat_wave        late-cycle heat -> heat stress, birds drink more and eat
//                    less, growth stalls and heat mortality rises
//                    (action: cooling / ventilation / water access).
//   poor_ventilation early minimum-ventilation air-quality build-up -> high CO2
//                    and high humidity (action: ventilation & litter).
//   feed_uniformity  uneven feed access -> low flock uniformity
//                    (action: feeder-line distribution).
//   healthy          on track (peak-growth info only).
const SCENARIO_ROTATION = ['heat_wave', 'poor_ventilation', 'feed_uniformity', 'healthy', 'heat_wave', 'poor_ventilation']

function flockScenario (index, currentAge) {
  const kind = SCENARIO_ROTATION[index % SCENARIO_ROTATION.length]
  let start = null
  let end = null
  if (kind === 'heat_wave') {
    // Late-cycle heat is the most dangerous: heavy birds shed heat poorly.
    end = currentAge - 1
    start = Math.max(6, end - 3)
  } else if (kind === 'poor_ventilation') {
    // Early minimum-ventilation window (cold outside -> vents kept low).
    start = Math.max(5, Math.min(9, currentAge - 10))
    end = Math.min(start + 5, currentAge - 2)
  } else if (kind === 'feed_uniformity') {
    start = Math.max(8, Math.round(currentAge * 0.45))
    end = Math.min(start + 4, currentAge - 2)
  }
  return { kind: kind, start: start, end: end }
}

function inScenarioWindow (age, scenario) {
  return scenario && scenario.start != null && age >= scenario.start && age <= scenario.end
}

// Scenario impact on daily weight gain. Anomalies leave a proportional mark on
// performance so the insights match what the flock chart shows:
//   heat_wave        severe late-cycle intake drop -> big gap vs reference.
//   poor_ventilation early air-quality stress -> growth setback the flock only
//                    partly compensates for afterwards (stays a bit behind).
//   feed_uniformity  uneven access -> a mild average-weight drag (plus wider CV).
function gainMultiplier (age, scenario) {
  if (!scenario) return 1
  if (scenario.kind === 'heat_wave' && inScenarioWindow(age, scenario)) {
    return 0.35
  }
  if (scenario.kind === 'poor_ventilation') {
    if (inScenarioWindow(age, scenario)) return 0.78
    if (age > scenario.end && age <= scenario.end + 4) return 1.12
  }
  if (scenario.kind === 'feed_uniformity' && inScenarioWindow(age, scenario)) {
    return 0.9
  }
  return 1
}

// Weighing history rows (ages 0..currentAge-1; the current age has no weighing
// yet, so the projection unlocks off the live age — mirrors real behaviour).
function buildWeightAnalysis (strain, currentAge, factor, seedKey, scenario) {
  const weights = curveWeights(strain)
  const rand = seeded(hashString('wa' + seedKey))
  const rows = []
  let prevAw = 0
  // Clean cumulative weight driven by daily reference gain x scenario impact, so
  // a setback carries forward (the flock does not snap back to the reference
  // curve the day after a stress window).
  let cleanAw = 0
  for (let age = 0; age < currentAge; age++) {
    const curveW = (weights[age] || 0) * factor
    const prevCurveW = age > 0 ? (weights[age - 1] || 0) * factor : 0
    const curveGain = Math.max(0, curveW - prevCurveW)
    cleanAw += curveGain * (age === 0 ? 1 : gainMultiplier(age, scenario))
    const scatter = 1 + (rand() - 0.5) * 0.012
    let aw = Math.round(cleanAw * scatter)
    if (prevAw && aw < prevAw) {
      aw = prevAw
    }
    const gain = age > 0 ? aw - prevAw : 0
    prevAw = aw
    let cv = Math.max(6.5, 9.5 - age * 0.06)
    // Uneven feed access lowers flock uniformity (higher CV) for a spell.
    if (scenario && scenario.kind === 'feed_uniformity' && inScenarioWindow(age, scenario)) {
      cv += 4
    }
    const sd = Math.round(aw * (cv / 100))
    // Weight distribution buckets with day-to-day scatter; the extremes shrink
    // as uniformity (cv) improves with age. Each row sums to 100.
    const wpvh = clamp(Number((cv * 1.1 + (rand() - 0.5) * 3).toFixed(1)), 4, 20)
    const wpvl = clamp(Number((cv * 1.3 + (rand() - 0.5) * 3).toFixed(1)), 5, 22)
    const wph = Number(((100 - wpvh - wpvl) * (0.5 + (rand() - 0.5) * 0.08)).toFixed(1))
    const wpl = Number((100 - wpvh - wpvl - wph).toFixed(1))
    rows.push({
      age: age,
      aw: aw,
      gain: gain,
      max_w: aw + 2 * sd,
      min_w: Math.max(aw - 2 * sd, 0),
      wc: Number(cv.toFixed(1)),
      sd: sd,
      wpvh: wpvh,
      wph: wph,
      wpl: wpl,
      wpvl: wpvl,
      rec_at: isoDate(currentAge - age)
    })
  }

  // Natural late-cycle gain taper (feed conversion slows near slaughter weight)
  // so the peak daily gain lands before the last weighing (peak-growth info).
  // Heat-wave flocks already stall late (growth-decline warning).
  if (rows.length >= 6 && !(scenario && scenario.kind === 'heat_wave')) {
    const peakGain = rows.reduce(function (max, row) { return row.gain > max ? row.gain : max }, 0)
    const last = rows[rows.length - 1]
    const prev = rows[rows.length - 2]
    last.gain = Math.round(peakGain * 0.72)
    last.aw = prev.aw + last.gain
    last.max_w = last.aw + 2 * last.sd
    last.min_w = Math.max(last.aw - 2 * last.sd, 0)
  }
  return rows
}

// Precomputed 5-day projection (option a). standard = recent daily-gain
// extrapolation from the last weighing; genetics = reference curve scaled to
// keep the flock's current ratio to the breed standard.
function buildProjection (strain, currentAge, factor, days, waRows) {
  const weights = curveWeights(strain)
  const anchorAge = currentAge - 1
  if (anchorAge < 24) {
    return { standard: [], genetics: [], usesCurve: false }
  }
  // Anchor on the flock's ACTUAL last weighing (which already carries any
  // scenario deficit) so the projected lines continue from the real data point,
  // not the untouched reference curve.
  const rows = waRows || []
  const lastRow = rows.length ? rows[rows.length - 1] : null
  const anchorWeight = lastRow ? lastRow.aw : Math.round((weights[anchorAge] || 0) * factor)
  const recent = rows.slice(-5)
  let totalGain = 0
  let steps = 0
  for (let i = 1; i < recent.length; i++) {
    totalGain += recent[i].aw - recent[i - 1].aw
    steps++
  }
  const gpdm = steps > 0
    ? Math.trunc(totalGain / steps)
    : (weights[anchorAge] && weights[anchorAge - 1] ? Math.round((weights[anchorAge] - weights[anchorAge - 1]) * factor) : 0)
  const ratio = weights[anchorAge] ? anchorWeight / weights[anchorAge] : 1
  const standard = []
  const genetics = []
  for (let i = 1; i <= days; i++) {
    const age = anchorAge + i
    standard.push({ age: age, aw: anchorWeight + gpdm * i })
    const curveAt = weights[age]
    genetics.push({ age: age, aw: curveAt ? Math.round(curveAt * ratio) : anchorWeight + gpdm * i })
  }
  return { standard: standard, genetics: genetics, usesCurve: true }
}

function buildMortalities (batchId, identifier, quantityHoused, currentAge, scenario) {
  const rand = seeded(hashString('mort' + batchId))
  const rows = []
  let acc = 0
  for (let age = 0; age < currentAge; age++) {
    // Elevated in the first days, then a low steady baseline, with daily scatter.
    const baseRate = age < 3 ? 0.0012 : age < 7 ? 0.0006 : 0.00033
    let natural = Math.max(1, Math.round(quantityHoused * baseRate * (0.55 + rand())))
    // Sporadic culling (leg/health) on roughly 1 in 4 days.
    const culling = rand() < 0.22
      ? Math.max(1, Math.round(quantityHoused * (0.00008 + rand() * 0.0002)))
      : 0
    // Heat mortality peaks on the hottest days of a heat wave (heavy birds are
    // most at risk), then eases.
    if (scenario && scenario.kind === 'heat_wave' && inScenarioWindow(age, scenario)) {
      const span = Math.max(1, scenario.end - scenario.start + 1)
      const intensity = (age - scenario.start + 1) / span
      natural += Math.round(quantityHoused * (0.0006 + intensity * 0.0018))
    }
    // Poor ventilation lifts early losses modestly (air-quality stress) without
    // the severity of a heat event; enough to nudge accumulated mortality up.
    if (scenario && scenario.kind === 'poor_ventilation' && inScenarioWindow(age, scenario)) {
      natural += Math.round(quantityHoused * 0.0003)
    }
    const quantity = natural + culling
    acc += quantity
    rows.push({
      id: batchId + '-mort-' + age,
      age: age,
      quantity: quantity,
      naturalQuantity: natural,
      cullingQuantity: culling,
      accumulated: acc,
      date: isoDate(currentAge - age),
      batch: { id: batchId, identifier: identifier }
    })
  }
  return rows
}

// Brooding temperature target by age: ~33 C on day 0 easing to a 21 C floor.
function tempTargetForAge (age) {
  return Math.max(21, 33 - age * 0.5)
}

// Daily environment max/min history (SMAAI 5 `statistics`, and InobramX
// internal `inobramx/maxmin`). Same field names for both device families, so
// the DailyMaxAndMin* charts read `t_max/t_min/h_max/h_min/co2_max/co2_min`
// and `h2o_acc` regardless of device. `dt/dh` are the SMAAI 5 setpoint lines.
function buildEnvironmentMaxMin (strain, currentAge, seedKey, scenario) {
  const weights = curveWeights(strain)
  const rand = seeded(hashString('env' + seedKey))
  const rows = []
  for (let age = 0; age <= currentAge; age++) {
    const target = tempTargetForAge(age)
    const tShift = (rand() - 0.5) * 1.6
    const tSpread = 1.1 + rand() * 0.8
    const humTarget = 60 + Math.min(age * 0.1, 6)
    const hShift = (rand() - 0.5) * 4
    const hSpread = 4 + rand() * 3
    const co2 = 900 + age * 18
    const co2Shift = (rand() - 0.5) * 120
    const co2Spread = 130 + rand() * 90
    const dailyFeedKg = Math.round((weights[age] || 0) * 0.115 * 20000 / 1000)
    let waterFactor = 1 + (rand() - 0.5) * 0.12
    const inWin = inScenarioWindow(age, scenario)
    // Heat wave: hot afternoons push temperature well above the desired setpoint
    // and birds drink markedly more.
    const heat = inWin && scenario.kind === 'heat_wave'
    const tHeat = heat ? 3 + (age - scenario.start) * 0.6 : 0
    if (heat) {
      waterFactor += 0.5
    }
    // Poor ventilation: minimum ventilation lets CO2 and humidity climb.
    const vent = inWin && scenario.kind === 'poor_ventilation'
    const co2Vent = vent ? 1900 + (age - scenario.start) * 120 : 0
    const humVent = vent ? 16 : 0
    rows.push({
      age: age,
      t_max: Number((target + tSpread + tShift + tHeat + (heat ? 1.5 : 0)).toFixed(1)),
      t_min: Number((target - tSpread + tShift + tHeat).toFixed(1)),
      h_max: Math.round(humTarget + hSpread + hShift + humVent),
      h_min: Math.round(humTarget - hSpread + hShift + humVent),
      co2_max: Math.round(co2 + co2Spread + co2Shift + co2Vent),
      co2_min: Math.round(co2 - co2Spread + co2Shift + co2Vent),
      h2o_acc: Math.round(dailyFeedKg * 1.8 * waterFactor),
      dt: Number(target.toFixed(1)),
      dh: Math.round(humTarget),
      rec_at: isoDate(currentAge - age)
    })
  }
  return rows
}

// External (weather) daily max/min for InobramX `inobramx/maxmin?location=external`.
// Independent of bird age; a gentle seasonal swing with cool nights and scatter.
function buildExternalMaxMin (currentAge, seedKey) {
  const rand = seeded(hashString('ext' + seedKey))
  const rows = []
  for (let age = 0; age <= currentAge; age++) {
    const seasonal = Math.sin(age / 6) * 3
    const shift = (rand() - 0.5) * 2
    // Outdoor humidity swings much wider than the controlled indoor air: humid
    // nights (high max) and drier afternoons (low min), so it clearly differs
    // from the internal humidity series.
    const humSeasonal = Math.cos(age / 5) * 7
    const humShift = (rand() - 0.5) * 9
    const hMax = clamp(Math.round(84 + humSeasonal + humShift + rand() * 6), 62, 98)
    const hMin = clamp(Math.round(50 + humSeasonal + humShift - rand() * 6), 28, hMax - 6)
    rows.push({
      age: age,
      t_max: Number((25 + seasonal + shift + rand() * 2).toFixed(1)),
      t_min: Number((14 + seasonal + shift - rand() * 2).toFixed(1)),
      h_max: hMax,
      h_min: hMin
    })
  }
  return rows
}

// InobramX daily water consumption (`inobramx/performance`, metric 'h2o'); each
// row is { age, value } (litres) consumed by DailyWaterConsumptionChart.
function buildInobramxWater (strain, currentAge, seedKey, scenario) {
  const weights = curveWeights(strain)
  const rand = seeded(hashString('water' + seedKey))
  const rows = []
  for (let age = 0; age <= currentAge; age++) {
    const dailyFeedKg = Math.round((weights[age] || 0) * 0.115 * 20000 / 1000)
    let factor = 1 + (rand() - 0.5) * 0.14
    // Heat wave: birds drink markedly more (thermoregulation).
    if (scenario && scenario.kind === 'heat_wave' && inScenarioWindow(age, scenario)) {
      factor += 0.5
    }
    rows.push({ age: age, value: Math.round(dailyFeedKg * 1.8 * factor) })
  }
  return rows
}

// InobramX daily setpoints (`inobramx/setpoint`, metric 't' or 'h'); each row
// is { age, value } consumed by alignSetpointsWithStatistics.
function buildSetpoints (currentAge, metric) {
  const rows = []
  for (let age = 0; age <= currentAge; age++) {
    rows.push({
      age: age,
      value: metric === 'h' ? 60 : Number(tempTargetForAge(age).toFixed(1))
    })
  }
  return rows
}

// Alarm history rows for the `alarms` endpoint. Spreads a handful of past
// alarms across the flock window (deterministic pseudo-random) plus a couple in
// the last 24 h so the aggregate "last 24h" view is never empty.
function buildAlarmHistory (mac, deviceId, barnId, base, currentAge) {
  const codes = base === 'inobramx'
    ? [1, 3, 5, 8, 200, 138]
    : [1, 3, 5, 8, 138]
  const seed = mac.charCodeAt(15) || 7
  const now = Date.now()
  const rows = []
  let seq = 0
  for (let daysAgo = currentAge; daysAgo >= 1; daysAgo--) {
    if (Math.abs(Math.sin((daysAgo + 1) * seed)) < 0.62) {
      continue
    }
    const ts = new Date(now - daysAgo * 86400000)
    ts.setHours(6 + (seq % 14), (seq * 7) % 60, 0, 0)
    rows.push({
      id: deviceId + '-alarm-' + seq,
      mac: mac,
      deviceId: deviceId,
      barnId: barnId,
      code: codes[seq % codes.length],
      rec_at: ts.toISOString()
    })
    seq++
  }
  for (let i = 0; i < 2; i++) {
    rows.push({
      id: deviceId + '-alarm-recent-' + i,
      mac: mac,
      deviceId: deviceId,
      barnId: barnId,
      code: base === 'inobramx' && i === 0 ? 200 : codes[i % codes.length],
      rec_at: new Date(now - (2 + i * 6) * 3600000).toISOString()
    })
  }
  return rows
}

// Live RTDB `general` node. Returns a function so values animate over the tick.
function buildLiveNode (nucleus, mac, age, factor, theaterRole) {
  const inobramx = nucleus.base === 'inobramx'
  const weights = curveWeights(nucleus.strain)
  const liveWeight = age != null ? Math.round((weights[age] || 0) * factor) : null
  const quantity = 20000
  // Flock feed/water so FCR/EPEF light up.
  const totalLiveKg = age != null ? (liveWeight / 1000) * quantity * 0.985 : 0
  const feedKg = age != null ? Math.round(totalLiveKg * (FCR_CURVE[age] || 1.6)) : 0
  const waterL = Math.round(feedKg * 1.8)
  const dailyFeedKg = age != null ? Math.round((weights[age] || 0) * 0.115 * quantity / 1000) : 0
  const dailyWaterL = Math.round(dailyFeedKg * 1.8)

  return function (tick) {
    // Alarm theater: cycle a high-temperature alarm and a low-feed warning so
    // they appear and clear live during the demo (each raised device plays one).
    const CYCLE = 24
    const phase = tick % CYCLE
    const highTempActive = theaterRole === 'high_temp' && phase >= 6 && phase < 12
    const lowFeedActive = theaterRole === 'low_feed' && phase >= 14 && phase < 20
    const wobble = Math.sin(tick / 4) * 0.35
    const wobble2 = Math.cos(tick / 5) * 0.5
    const baseTemp = highTempActive ? 32.6 : (26.5 - age * 0.12)
    const temp = age == null ? 21.0 : Number((baseTemp + wobble).toFixed(1))
    const hum = age == null ? 55 : Math.round(62 + age * 0.15 + wobble2)
    const co2 = age == null ? 480 : Math.round(1200 + age * 12 + wobble2 * 20)
    const pressure = Math.round(24 + wobble2)

    const base = {
      mac: mac,
      age: age,
      rec_at: new Date().toISOString(),
      dt: age == null ? 22 : Number((26.5 - age * 0.12).toFixed(1)),
      dh: 60,
      dp: 25,
      // Card average weight: inobramx reads `aw` from the `general` node;
      // SMAAI 5 reads it from the `weightAnalysisPerHour` child.
      aw: liveWeight,
      updatedAt: Date.now(),
      weightAnalysisPerHour: liveWeight != null
        ? { aw: liveWeight, updatedAt: Date.now() }
        : null
    }

    if (inobramx) {
      return Object.assign(base, {
        at_i: temp,
        ah_i: hum,
        p1: pressure,
        aco2: co2,
        h2o_acc: dailyWaterL,
        h2o_lot: waterL,
        sps1: age == null ? 0 : (lowFeedActive ? 850 + Math.round(wobble * 20) : 8200 + Math.round(wobble * 40)),
        sps2: age == null ? 0 : 7600 + Math.round(wobble2 * 40),
        sps3: age == null ? 0 : 6100,
        sps_c_lot: feedKg,
        sps1_c: Math.round(dailyFeedKg * 0.4),
        sps2_c: Math.round(dailyFeedKg * 0.35),
        sps3_c: Math.round(dailyFeedKg * 0.25),
        a_a_c: lowFeedActive ? '[200]' : '[]'
      })
    }

    return Object.assign(base, {
      temperature: temp,
      humidity: hum,
      pressure: pressure,
      air_co21: co2,
      co2: co2,
      h2o_acc: dailyWaterL,
      h2o1_acc_lot: Math.round(waterL * 0.55),
      h2o2_acc_lot: Math.round(waterL * 0.45),
      h2o3_acc_lot: 0,
      h2o_lot: waterL,
      sps1: age == null ? 0 : 9100 + Math.round(wobble * 40),
      sps2: age == null ? 0 : 8300 + Math.round(wobble2 * 40),
      sps1_c_lot: Math.round(feedKg * 0.55),
      sps2_c_lot: Math.round(feedKg * 0.45),
      alarm_high_temperature: highTempActive ? 2 : 0,
      alarm_low_temperature: 0,
      alarm_high_humidity: 0,
      alarm_low_humidity: 0,
      alarm_high_co2: 0,
      alarm_high_pressure: 0,
      alarm_low_pressure: 0,
      alarm_disarm: 0,
      alarm_low_source: 0
    })
  }
}

let deviceIndex = 0
let highTempAssigned = false
let lowFeedAssigned = false
let runningFlockIndex = 0

topology.forEach(function (prop) {
  const property = {
    id: prop.id,
    description: prop.description,
    country: 'Brasil',
    state: 'Santa Catarina',
    city: prop.city.description,
    address: 'Linha ' + prop.description,
    nuclei: []
  }

  prop.nuclei.forEach(function (nuc) {
    const nucleusRef = { id: nuc.id, description: nuc.description, property: { id: prop.id, description: prop.description } }
    const nucleusNode = { id: nuc.id, description: nuc.description, property: { id: prop.id, description: prop.description, city: prop.city, state: STATE_SC, country: COUNTRY_BR }, barns: [] }

    for (let b = 0; b < 2; b++) {
      const barnId = nuc.id + '-barn-' + (b + 1)
      const mac = macFor(deviceIndex)
      const age = nuc.ages[b]
      const factor = [0.99, 1.03, 0.97, 1.02][deviceIndex % 4]
      deviceIndex++

      const deviceModel = { id: 'model-' + nuc.model, description: nuc.model, pretty_name: nuc.pretty, category: 'controller' }
      const barnMini = { id: barnId, description: 'Galpão ' + (b + 1) }
      const device = {
        id: 'dev-' + mac.replace(/:/g, ''),
        description: nuc.pretty + ' ' + (b + 1),
        model: nuc.model,
        type: nuc.model,
        identifier_value: mac,
        base: nuc.base,
        token: 'demo' + deviceIndex,
        created_at: isoDate(120),
        device_model: deviceModel,
        permissions: [{ permission: 'owner', user: { id: DEMO_USER.uid } }],
        barn: {
          id: barnId,
          description: barnMini.description,
          nucleus: { id: nuc.id, description: nuc.description, property: { id: prop.id, description: prop.description } }
        }
      }

      const barnFull = {
        id: barnId,
        description: barnMini.description,
        nucleus: nucleusRef,
        permissions: [{ permission: 'owner', user: { id: DEMO_USER.uid } }],
        details: [{ id: barnId + '-detail', description: barnMini.description }],
        devices: [device]
      }

      devicesFlat.push(device)
      barnsFlat.push(barnFull)
      nucleusNode.barns.push(barnFull)

      // Silos (enabled -> unlocks FCR/EPEF).
      silosByBarnId[barnId] = [
        { id: barnId + '-silo-1', barn: { id: barnId }, capacity: 20000, critical_level: 3000, enabled: true, siloNumber: 1 },
        { id: barnId + '-silo-2', barn: { id: barnId }, capacity: 20000, critical_level: 3000, enabled: true, siloNumber: 2 }
      ]

      // Every device has all experimental features enabled.
      deviceFeatures.push({
        deviceId: device.id,
        features: [{
          feature_flag: 'platform',
          attributes: [
            { key: 'alarms', value: 'true' },
            { key: 'bird_weight', value: 'true' },
            { key: 'comfort_metrics', value: 'true' },
            { key: 'batch_insights', value: 'true' },
            { key: 'correlation_charts', value: 'true' }
          ]
        }]
      })

      currentAgeByMac[mac] = age
      flockFactorByMac[mac] = factor
      let theaterRole = null
      if (age != null && !nuc.clear) {
        if (nuc.base !== 'inobramx' && !highTempAssigned) {
          theaterRole = 'high_temp'
          highTempAssigned = true
        } else if (nuc.base === 'inobramx' && !lowFeedAssigned) {
          theaterRole = 'low_feed'
          lowFeedAssigned = true
        }
      }
      liveNodesByMac[mac] = buildLiveNode(nuc, mac, age, factor, theaterRole)

      if (nuc.clear || age == null) {
        batchesByBarnId[barnId] = []
      } else {
        const batchId = barnId + '-flock'
        // Flock numbering is per barn (each barn currently has a single active
        // flock, so it is its flock 01).
        const batchIdentifier = '01'
        const initialDaysAgo = age
        const scenario = flockScenario(runningFlockIndex, age)
        runningFlockIndex += 1
        batchesByBarnId[barnId] = [{
          id: batchId,
          identifier: batchIdentifier,
          status: 'in_progress',
          initial_date: isoDate(initialDaysAgo) + 'T00:00:00.000Z',
          final_date: null,
          quantity_housed: 20000,
          processing_plant: 1,
          sex: 'as_hatched',
          genetics: nuc.strain,
          barn: { id: barnId, description: barnMini.description, devices: [device] }
        }]
        weightAnalysisByBatchId[batchId] = buildWeightAnalysis(nuc.strain, age, factor, batchId, scenario)
        projectionByBatchId[batchId] = buildProjection(nuc.strain, age, factor, 5, weightAnalysisByBatchId[batchId])
        mortalitiesByBatchId[batchId] = buildMortalities(batchId, batchIdentifier, 20000, age, scenario)

        // Environment history (Graficos > Ambiente) + alarm history (Alarmes >
        // Historico) for the running flock.
        if (nuc.base === 'inobramx') {
          inobramxMaxMinByMac[mac] = buildEnvironmentMaxMin(nuc.strain, age, mac, scenario)
          inobramxExternalMaxMinByMac[mac] = buildExternalMaxMin(age, mac)
          inobramxTempSetpointsByMac[mac] = buildSetpoints(age, 't')
          inobramxHumSetpointsByMac[mac] = buildSetpoints(age, 'h')
          inobramxWaterByMac[mac] = buildInobramxWater(nuc.strain, age, mac, scenario)
        } else {
          statisticsByMac[mac] = buildEnvironmentMaxMin(nuc.strain, age, mac, scenario)
        }
        buildAlarmHistory(mac, device.id, barnId, nuc.base, age).forEach(function (record) {
          alarmHistoryAll.push(record)
        })
      }
    }

    property.nuclei.push(nucleusNode)
    nucleiFlat.push({ id: nuc.id, description: nuc.description, property: { id: prop.id, description: prop.description } })
  })

  properties.push(property)
})



// WEBPACK FOOTER //
// ./src/demo/dataset.js